import { API_BASE_URL } from "../config/env";
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "../storage/token.storage";
import { TokenResponse } from "../types/auth.types";
import { ApiErrorResponse } from "../types/api.types";

type RequestMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface RequestOptions {
    method?: RequestMethod;
    body?: BodyInit | null;
    headers?: Record<string, string>;
    auth?: boolean;
}

const buildErrorMessage = async (response: Response): Promise<string> => {
    try {
        const data: ApiErrorResponse = await response.json();

        if (typeof data.detail === "string") {
            return data.detail;
        }

        if (Array.isArray(data.detail)) {
            return data.detail.map((item) => item.msg).filter(Boolean).join(", ");
        }

        // Некоторые эндпоинты (например удаление билета из заказа) возвращают
        // detail объектом вида { code, message } — показываем человекопонятное message, если оно есть.
        if (data.detail && typeof data.detail === "object" && typeof (data.detail as any).message === "string") {
            return (data.detail as any).message;
        }

        return "Произошла ошибка запроса";
    } catch {
        return "Произошла ошибка запроса";
    }
};

const refreshAccessToken = async (): Promise<string | null> => {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
        return null;
    }

    const response = await fetch(
        `${API_BASE_URL}/auth/refresh?refresh_token=${encodeURIComponent(refreshToken)}`,
        {
            method: "POST",
        }
    );

    if (!response.ok) {
        clearTokens();
        return null;
    }

    const tokenData: TokenResponse = await response.json();
    setTokens(tokenData.access_token, tokenData.refresh_token);

    return tokenData.access_token;
};

export const apiRequest = async <T>(
    endpoint: string,
    options: RequestOptions = {}
): Promise<T> => {
    const {
        method = "GET",
        body = null,
        headers = {},
        auth = true,
    } = options;

    const requestHeaders: Record<string, string> = {
        ...headers,
    };

    if (auth) {
        const token = getAccessToken();

        if (token) {
            requestHeaders.Authorization = `Bearer ${token}`;
        }
    }

    let response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method,
        headers: requestHeaders,
        body,
    });

    if (response.status === 401 && auth) {
        const refreshedToken = await refreshAccessToken();

        if (refreshedToken) {
            requestHeaders.Authorization = `Bearer ${refreshedToken}`;

            response = await fetch(`${API_BASE_URL}${endpoint}`, {
                method,
                headers: requestHeaders,
                body,
            });
        }
    }

    if (!response.ok) {
        const errorMessage = await buildErrorMessage(response);
        throw new Error(errorMessage);
    }

    if (response.status === 204) {
        return {} as T;
    }

    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
        return response.json() as Promise<T>;
    }

    return {} as T;
};
